/**
 * The read-only preview's own state: WHICH SCREEN IS ON SHOW.
 *
 * TWO RULES SHAPE EVERY LINE OF THIS FILE.
 *
 * 1. READ-ONLY (§A-4, T13 §2-1). The preview never calls a store action. It reads getters and it
 *    OBSERVES (`watch`, `$onAction`), which is not the same as calling. The stepper's position is
 *    therefore this hook's state, never the store's `selectedScreenId`.
 * 2. THE DOCUMENT IS NOT VALID, AND THAT IS NORMAL. `screens: []` is what `create` produces, and a
 *    malformed `screens` could be anything, so every read below is defensive: a preview that
 *    throws mid-edit takes the editor beside it down with it.
 *
 * The stepper FOLLOWS the editor's selection (§3b) by a one-way read — the question it answers is
 * "what does the screen I am editing look like". The reverse would be a `selectScreen` call, which
 * §2-1 forbids, so prev/next are a transient local override that the next selection snaps back.
 *
 * FOLLOWING IS BY ID, NEVER BY POSITION (the T13 review's critical finding). Caching an index and
 * refreshing it from a `watch` on `getSelectedScreenId` is wrong on the happy path: `moveScreenUp`
 * / `moveScreenDown` reorder `screens` in place without touching `selectedScreenId`, so the watch
 * never refires and the cached index addresses a different screen — unrecoverably, since
 * re-selecting does not change the watched value either. So the state is an ID
 * (`overrideScreenId`, `null` while following) and the index is re-derived on every read.
 *
 * Two things clear the override: the `watch` (the selection CHANGED — rail click, `addScreen`,
 * `removeScreen`'s prune) and the `$onAction` observer (`selectScreen` was CALLED AT ALL, including
 * with the id already selected, which the store returns early on so no watch can see it).
 *
 * A followed screen that no longer exists falls back to the selection, then to the first screen —
 * never to a stale position.
 */
import { computed, ref, watch } from 'vue'

import type { ComputedRef } from 'vue'
import type { Screen } from './interfaces'

import { useAssessmentEditorStore } from './store'

interface UseAssessmentPreviewReturn {
  refScreens: ComputedRef<Screen[]>
  /** The screen on show — `null` when there are none. */
  refScreen: ComputedRef<Screen | null>
  /** 0-based index, re-derived from the followed **id** on every read. */
  refIndex: ComputedRef<number>
  /** 1-based, as an author counts screens. `0` when there are none. */
  refPosition: ComputedRef<number>
  refCount: ComputedRef<number>
  refHasScreens: ComputedRef<boolean>
  refIsFirst: ComputedRef<boolean>
  refIsLast: ComputedRef<boolean>
  /** False only while the prev/next buttons have stepped away from the editor's selection. */
  refIsFollowingSelection: ComputedRef<boolean>
  goPrevious: () => void
  goNext: () => void
}

export const useAssessmentPreview = (): UseAssessmentPreviewReturn => {
  const store = useAssessmentEditorStore()

  /** `getScreens` defaults to `[]`, but a malformed document can hold a non-array `screens` (B7's
   *  map-vs-list hazard), where `.length` is `undefined` rather than a throw — worse, because it
   *  renders nonsense instead of an empty state. Normalise once, here. */
  const refScreens = computed<Screen[]>(() => {
    const screens: unknown = store.getScreens
    return Array.isArray(screens) ? screens as Screen[] : []
  })

  const refCount = computed<number>(() => refScreens.value.length)

  const refHasScreens = computed<boolean>(() => refCount.value > 0)

  /** The preview's ENTIRE state: the id of the screen the author stepped to, or `null` while
   *  following the editor's selection. An id, deliberately — see the header. */
  const overrideScreenId = ref<string | null>(null)

  const indexOf = (screenId: string | null): number =>
    (screenId === null ? -1 : refScreens.value.findIndex(screen => screen?.id === screenId))

  const refSelectedIndex = computed<number>(() => indexOf(store.getSelectedScreenId))

  const refIndex = computed<number>(() => {
    if (!refHasScreens.value) {
      return 0
    }
    // Override, then selection, then the first screen. Each step re-resolves an id against the
    // CURRENT array, so a reorder moves the preview and a removal falls through to something real.
    const override = indexOf(overrideScreenId.value)
    if (override >= 0) {
      return override
    }
    return refSelectedIndex.value >= 0 ? refSelectedIndex.value : 0
  })

  const refScreen = computed<Screen | null>(() => refScreens.value[refIndex.value] ?? null)

  const refPosition = computed<number>(() => (refHasScreens.value ? refIndex.value + 1 : 0))

  const refIsFirst = computed<boolean>(() => refIndex.value === 0)

  const refIsLast = computed<boolean>(() => refIndex.value >= refCount.value - 1)

  /** Following whenever the preview is showing the selected screen — including when the override
   *  id resolves to it, and when the selection cannot be resolved at all. */
  const refIsFollowingSelection = computed<boolean>(() =>
    refSelectedIndex.value < 0 || refSelectedIndex.value === refIndex.value,
  )

  /** Stepping onto the selected screen resumes following rather than pinning an override that
   *  happens to agree — otherwise the next reorder would be an override, not following. */
  const stepTo = (index: number): void => {
    const screenId = refScreens.value[index]?.id ?? null
    overrideScreenId.value = screenId === store.getSelectedScreenId ? null : screenId
  }

  const goPrevious = (): void => {
    stepTo(Math.max(refIndex.value - 1, 0))
  }

  const goNext = (): void => {
    stepTo(Math.min(refIndex.value + 1, Math.max(refCount.value - 1, 0)))
  }

  /** The selection CHANGED — rail click, `addScreen`, or `removeScreen` pruning it. A read; there
   *  is deliberately no watcher the other way, which would mean calling `selectScreen`. */
  watch(
    () => store.getSelectedScreenId,
    () => {
      overrideScreenId.value = null
    },
  )

  /** `selectScreen` was CALLED — including with the id already selected, which the store handles
   *  by returning early, so the watch above cannot see it. Observing another caller's action is
   *  not calling one; the subscription is disposed with the scope. */
  store.$onAction(({ name }) => {
    if (name === 'selectScreen') {
      overrideScreenId.value = null
    }
  })

  return {
    refScreens,
    refScreen,
    refIndex,
    refPosition,
    refCount,
    refHasScreens,
    refIsFirst,
    refIsLast,
    refIsFollowingSelection,
    goPrevious,
    goNext,
  }
}
